import * as React from 'react';
import { SelectCheckbox } from './SelectCheckbox';
import { useSelectOpen } from './SelectContext';
import type { SelectItemRenderer, SelectOption } from './types';

interface FlatSelectContentProps<ItemType extends string> {
  options: SelectOption<ItemType>[];
  selectedItems: ItemType[];
  onToggle: (value: ItemType) => void;
  renderItem?: SelectItemRenderer<ItemType>;
  emptyState?: React.ReactNode;
  selectId?: string;
  selectionMode?: 'single' | 'multiple';
}

export function FlatSelectContent<ItemType extends string>({
  options,
  selectedItems,
  onToggle,
  renderItem = undefined,
  emptyState = undefined,
  selectId = undefined,
  selectionMode = 'multiple',
}: FlatSelectContentProps<ItemType>) {
  const open = useSelectOpen();
  const wasOpen = React.useRef(false);
  const [orderedOptions, setOrderedOptions] = React.useState(options);

  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setOrderedOptions(
        [...options].sort(
          (left, right) => Number(selectedItems.includes(right.value!)) - Number(selectedItems.includes(left.value!))
        )
      );
    }
    wasOpen.current = open;
  }, [open, options, selectedItems]);

  React.useEffect(() => {
    setOrderedOptions(current => {
      const available = new Map(options.map(option => [option.value, option]));
      const ordered = current
        .map(option => available.get(option.value))
        .filter((option): option is SelectOption<ItemType> => option !== undefined);
      const currentValues = new Set(ordered.map(option => option.value));

      return [...ordered, ...options.filter(option => !currentValues.has(option.value))];
    });
  }, [options]);

  return (
    <div className="py-1">
      {orderedOptions.map((option, index) => {
        const value = option.value;
        if (value === undefined) return null;
        const selected = selectedItems.includes(value);

        return renderItem ? (
          <div key={value} role="option" aria-selected={selected} onClick={() => onToggle(value)}>
            {renderItem({ option, selected, index })}
          </div>
        ) : (
          <div
            key={value}
            data-testid={selectId ? `${selectId}-option-${value}` : 'select-option'}
            className="flex h-12 min-h-12 w-full cursor-pointer items-center gap-2 px-3 py-4 text-left text-sm hover:bg-snow-100 aria-selected:bg-snow-200"
            role="option"
            aria-selected={selected}
            tabIndex={0}
            onClick={() => onToggle(value)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggle(value);
              }
            }}
          >
            {selectionMode === 'multiple' ? (
              <SelectCheckbox checked={selected} />
            ) : (
              <span className="flex h-5 w-5 items-center justify-center">
                {selected && <span className="text-base font-semibold">✓</span>}
              </span>
            )}
            <span>{option.label}</span>
          </div>
        );
      })}
      {emptyState}
    </div>
  );
}
